import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Receipt, Plus, Loader2, AlertTriangle, DollarSign, Clock, CheckCircle, Building, CreditCard, Upload, Image, X, Trash2, Eye, FileText, RefreshCw, Download, FileSpreadsheet, Calendar, Settings, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

// Helper to format currency based on the currency type
const formatCurrency = (val, moneda = "RD$") => {
  const amount = val || 0;
  // Map currency codes to their symbols and formats
  const currencyMap = {
    "RD$": { code: "DOP", symbol: "RD$" },
    "US$": { code: "USD", symbol: "US$" },
    "EUR$": { code: "EUR", symbol: "€" }
  };
  const curr = currencyMap[moneda] || currencyMap["RD$"];
  
  // Format the number with 2 decimal places
  const formatted = amount.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${curr.symbol}${formatted}`;
};

export default function CuentasPagarPage() {
  const [dashboard, setDashboard] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showFacturaDialog, setShowFacturaDialog] = useState(false);
  const [showPagoDialog, setShowPagoDialog] = useState(false);
  const [showDetalleDialog, setShowDetalleDialog] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [filterEstado, setFilterEstado] = useState("all");
  const [filterProveedor, setFilterProveedor] = useState("all");
  const [filterVencimiento, setFilterVencimiento] = useState("all");
  const [exportingExcel, setExportingExcel] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  // Ordenamiento dinámico por encabezado. Default: por vencimiento ascendente
  // (las que vencen primero / vencidas arriba), con las pagadas al final.
  const [sortField, setSortField] = useState("default");
  const [sortDir, setSortDir] = useState("asc");
  // Para la columna Estado: cada clic rota qué grupo de estado se fija arriba.
  const [estadoCycle, setEstadoCycle] = useState(0);
  const limit = 100000; // se cargan todas las facturas del filtro; paginado client-side
  
  // Monedas disponibles
  const MONEDAS = [
    { value: "RD$", label: "RD$ (Peso Dominicano)" },
    { value: "US$", label: "US$ (Dólar)" },
    { value: "EUR$", label: "EUR$ (Euro)" }
  ];
  
  const [facturaForm, setFacturaForm] = useState({ 
    proveedor_id: "", 
    numero_factura: "", 
    tiene_comprobante_fiscal: "no",
    comercio_ncf: "",
    numero_ncf: "",
    transaccion: "",
    monto_total: 0, 
    moneda: "RD$",
    tasa_cambio: 0,
    fecha_factura: new Date().toISOString().split('T')[0], 
    fecha_vencimiento: "", 
    descripcion: "", 
    categoria: "servicios", 
    notas: "" 
  });
  const [pagoForm, setPagoForm] = useState({ monto: 0, fecha_pago: new Date().toISOString().split('T')[0], metodo_pago: "", numero_referencia: "", terminal_tarjeta: "", notas: "" });
  const [facturaFile, setFacturaFile] = useState(null);
  
  // Edit mode state
  const [editingFactura, setEditingFactura] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Configuración dialog
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configTab, setConfigTab] = useState("categorias");
  const [newCategoria, setNewCategoria] = useState({ nombre: "", descripcion: "" });
  const [newMoneda, setNewMoneda] = useState({ codigo: "", nombre: "", simbolo: "" });
  const [monedasConfig, setMonedasConfig] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Build query params
      let facturaParams = `skip=0&limit=${limit}`;
      if (filterEstado !== 'all') facturaParams += `&estado=${filterEstado}`;
      if (filterProveedor !== 'all') facturaParams += `&proveedor_id=${filterProveedor}`;
      if (filterVencimiento !== 'all') facturaParams += `&dias_vencimiento=${filterVencimiento}`;
      
      const [dashRes, factRes, provRes, catRes, metRes] = await Promise.all([
        axios.get(`${API}/cuentas-pagar/dashboard`, getAuthHeaders()),
        axios.get(`${API}/cuentas-pagar/facturas?${facturaParams}`, getAuthHeaders()),
        axios.get(`${API}/cuentas-pagar/proveedores`, getAuthHeaders()),
        axios.get(`${API}/cuentas-pagar/categorias`, getAuthHeaders()),
        axios.get(`${API}/cuentas-pagar/metodos-pago`, getAuthHeaders())
      ]);
      setDashboard(dashRes.data);
      setFacturas(factRes.data.items || []);
      setTotal(factRes.data.total || 0);
      setProveedores(provRes.data.items || []);
      setCategorias(catRes.data || []);
      setMetodosPago(metRes.data || []);
    } catch (err) { toast.error("Error al cargar datos"); }
    finally { setLoading(false); }
  }, [getAuthHeaders, filterEstado, filterProveedor, filterVencimiento]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateFactura = async () => {
    if (!facturaForm.proveedor_id || !facturaForm.numero_factura || facturaForm.monto_total <= 0 || !facturaForm.fecha_vencimiento) {
      toast.error("Complete todos los campos obligatorios"); return;
    }
    
    // Validar NCF si tiene comprobante fiscal
    if (facturaForm.tiene_comprobante_fiscal === "si" && !facturaForm.numero_ncf) {
      toast.error("Ingrese el número de NCF"); return;
    }
    
    if (facturaForm.moneda !== "RD$" && (!facturaForm.tasa_cambio || facturaForm.tasa_cambio <= 0)) {
      toast.error("Ingrese la tasa del día para la conversión a RD$"); return;
    }
    
    setSaving(true);
    try {
      // Preparar datos con el formato de comprobante fiscal
      const dataToSend = {
        ...facturaForm,
        comprobante_fiscal: facturaForm.tiene_comprobante_fiscal === "si" ? facturaForm.numero_ncf : "",
        comercio_ncf: facturaForm.tiene_comprobante_fiscal === "si" ? facturaForm.comercio_ncf : "",
        transaccion: facturaForm.tiene_comprobante_fiscal === "si" ? facturaForm.transaccion : "",
        tasa_cambio: facturaForm.moneda !== "RD$" ? (facturaForm.tasa_cambio || 0) : null,
        monto_rd: facturaForm.moneda !== "RD$" ? Math.round(facturaForm.monto_total * (facturaForm.tasa_cambio || 0) * 100) / 100 : null
      };
      
      let res;
      if (isEditing && editingFactura) {
        res = await axios.put(`${API}/cuentas-pagar/facturas/${editingFactura.id}`, dataToSend, getAuthHeaders());
      } else {
        res = await axios.post(`${API}/cuentas-pagar/facturas`, dataToSend, getAuthHeaders());
      }
      
      if (facturaFile && (res.data.id || editingFactura?.id)) {
        const facturaId = res.data.id || editingFactura.id;
        const formData = new FormData();
        formData.append("file", facturaFile);
        await axios.post(`${API}/cuentas-pagar/facturas/${facturaId}/upload`, formData, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" } });
      }
      toast.success(isEditing ? "Factura actualizada" : "Factura registrada");
      setShowFacturaDialog(false);
      setIsEditing(false);
      setEditingFactura(null);
      resetFacturaForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar factura"); }
    finally { setSaving(false); }
  };
  
  const resetFacturaForm = () => {
    setFacturaForm({ 
      proveedor_id: "", 
      numero_factura: "", 
      tiene_comprobante_fiscal: "no",
      comercio_ncf: "",
      numero_ncf: "",
      transaccion: "",
      monto_total: 0, 
      moneda: "RD$",
      tasa_cambio: 0,
      fecha_factura: new Date().toISOString().split('T')[0], 
      fecha_vencimiento: "", 
      descripcion: "", 
      categoria: "servicios", 
      notas: "" 
    });
    setFacturaFile(null);
  };
  
  const handleEditFactura = (factura) => {
    setEditingFactura(factura);
    setIsEditing(true);
    setFacturaForm({
      proveedor_id: factura.proveedor_id || "",
      numero_factura: factura.numero_factura || "",
      tiene_comprobante_fiscal: factura.comprobante_fiscal ? "si" : "no",
      comercio_ncf: factura.comercio_ncf || "",
      numero_ncf: factura.comprobante_fiscal || "",
      transaccion: factura.transaccion || "",
      monto_total: factura.monto_total || 0,
      moneda: factura.moneda || "RD$",
      tasa_cambio: factura.tasa_cambio || 0,
      fecha_factura: factura.fecha_factura || new Date().toISOString().split('T')[0],
      fecha_vencimiento: factura.fecha_vencimiento || "",
      descripcion: factura.descripcion || "",
      categoria: factura.categoria || "servicios",
      notas: factura.notas || ""
    });
    setShowFacturaDialog(true);
  };
  
  const handleOpenNewFactura = () => {
    setIsEditing(false);
    setEditingFactura(null);
    resetFacturaForm();
    setShowFacturaDialog(true);
  };

  const handleRegistrarPago = async () => {
    if (!selectedFactura || pagoForm.monto <= 0 || !pagoForm.metodo_pago) {
      toast.error("Complete todos los campos"); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/cuentas-pagar/pagos`, { factura_id: selectedFactura.id, ...pagoForm }, getAuthHeaders());
      toast.success("Pago registrado");
      setShowPagoDialog(false);
      setPagoForm({ monto: 0, fecha_pago: new Date().toISOString().split('T')[0], metodo_pago: "", numero_referencia: "", terminal_tarjeta: "", notas: "" });
      fetchData();
      if (showDetalleDialog) { viewFacturaDetalle(selectedFactura.id); }
    } catch (err) { toast.error(err.response?.data?.detail || "Error al registrar pago"); }
    finally { setSaving(false); }
  };

  const viewFacturaDetalle = async (facturaId) => {
    try {
      const res = await axios.get(`${API}/cuentas-pagar/facturas/${facturaId}`, getAuthHeaders());
      setSelectedFactura(res.data);
      setShowDetalleDialog(true);
    } catch (err) { toast.error("Error al cargar detalle"); }
  };

  const handleDeleteFactura = async (facturaId) => {
    if (!window.confirm("¿Está seguro de eliminar esta factura?")) return;
    try {
      await axios.delete(`${API}/cuentas-pagar/facturas/${facturaId}`, getAuthHeaders());
      toast.success("Factura eliminada");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  // Export to Excel function
  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      // Get the filter label for the report title
      const vencimientoLabel = filterVencimiento === 'all' ? 'Todas' : 
        filterVencimiento === '3' ? 'Próximos 3 días' :
        filterVencimiento === '7' ? 'Próximos 7 días' :
        filterVencimiento === '10' ? 'Próximos 10 días' :
        filterVencimiento === 'vencidas' ? 'Vencidas' : 'Todas';
      
      const estadoLabel = filterEstado === 'all' ? '' : ` - Estado: ${filterEstado}`;
      const titulo = `Cuentas por Pagar - ${vencimientoLabel}${estadoLabel}`;
      
      // Build query params for export (get all matching records, not paginated)
      let exportParams = '';
      if (filterEstado !== 'all') exportParams += `&estado=${filterEstado}`;
      if (filterProveedor !== 'all') exportParams += `&proveedor_id=${filterProveedor}`;
      if (filterVencimiento !== 'all') exportParams += `&dias_vencimiento=${filterVencimiento}`;
      
      const res = await axios.get(
        `${API}/cuentas-pagar/facturas/export-excel?${exportParams.slice(1)}&titulo=${encodeURIComponent(titulo)}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Download the file
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cuentas_por_pagar_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Reporte exportado exitosamente");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Error al exportar reporte");
    } finally {
      setExportingExcel(false);
    }
  };

  // ===== Ordenamiento dinámico (client-side) =====
  // Estado normalizado para ordenar/agrupar por la columna Estado.
  const getEstadoKey = (f) => {
    if (f.estado === "pagada") return "pagada";
    if (f.estado === "parcial") return "parcial";
    if (f.estado_alerta === "vencida") return "vencida";
    if (f.estado_alerta === "critica") return "por_vencer";
    return "pendiente";
  };
  // Rango por urgencia (para el orden por defecto): vencidas y próximas primero,
  // pagadas al final.
  const ESTADO_RANK = { vencida: 0, por_vencer: 1, pendiente: 2, parcial: 3, pagada: 4 };
  // Secuencia de "grupo fijado arriba" al hacer clic repetido en Estado.
  const ESTADO_CYCLE = ["vencida", "por_vencer", "pendiente", "parcial", "pagada"];

  const getSaldo = (f) => (f.monto_total || 0) - (f.monto_pagado || 0);

  const sortValue = (f, field) => {
    switch (field) {
      case "factura": return (f.numero_factura || "").toLowerCase();
      case "comprobante": return (f.comprobante_fiscal || "").toLowerCase();
      case "proveedor": return (f.proveedor_nombre || "").toLowerCase();
      case "categoria": return (categorias.find(c => c.value === f.categoria)?.label || f.categoria || "").toLowerCase();
      case "vencimiento": return f.fecha_vencimiento || "";
      case "monto": return f.monto_total || 0;
      case "moneda": return (f.moneda || "").toLowerCase();
      case "saldo": return getSaldo(f);
      default: return "";
    }
  };

  const sortedFacturas = (() => {
    const arr = [...facturas];
    if (sortField === "default") {
      // Urgencia (vencidas/por vencer primero, pagadas al final) y luego por
      // fecha de vencimiento más próxima.
      arr.sort((a, b) => {
        const ra = ESTADO_RANK[getEstadoKey(a)] ?? 9;
        const rb = ESTADO_RANK[getEstadoKey(b)] ?? 9;
        if (ra !== rb) return ra - rb;
        return (a.fecha_vencimiento || "").localeCompare(b.fecha_vencimiento || "");
      });
    } else if (sortField === "estado") {
      // Cada clic fija un grupo de estado arriba (cicla por ESTADO_CYCLE).
      const pinned = ESTADO_CYCLE[estadoCycle % ESTADO_CYCLE.length];
      arr.sort((a, b) => {
        const ka = getEstadoKey(a), kb = getEstadoKey(b);
        const pa = ka === pinned ? 0 : 1;
        const pb = kb === pinned ? 0 : 1;
        if (pa !== pb) return pa - pb;
        const ra = ESTADO_RANK[ka] ?? 9, rb = ESTADO_RANK[kb] ?? 9;
        if (ra !== rb) return ra - rb;
        return (a.fecha_vencimiento || "").localeCompare(b.fecha_vencimiento || "");
      });
    } else {
      arr.sort((a, b) => {
        const va = sortValue(a, sortField);
        const vb = sortValue(b, sortField);
        let cmp;
        if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
        else cmp = String(va).localeCompare(String(vb));
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return arr;
  })();

  const totalPages = Math.max(1, Math.ceil(sortedFacturas.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedFacturas = sortedFacturas.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  const handleSort = (field) => {
    setPage(0);
    if (field === "estado") {
      if (sortField === "estado") {
        setEstadoCycle((c) => c + 1);
      } else {
        setSortField("estado");
        setEstadoCycle(0);
      }
      return;
    }
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIndicator = ({ field }) => {
    if (field === "estado" && sortField === "estado") {
      return <span className="ml-1 text-[10px] text-blue-600 font-normal">({ESTADO_CYCLE[estadoCycle % ESTADO_CYCLE.length].replace("_", " ")})</span>;
    }
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 inline ml-1 text-slate-400" />;
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 inline ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 inline ml-1 text-blue-600" />;
  };

  const getEstadoBadge = (factura) => {
    if (factura.estado === "pagada") return <Badge className="bg-green-100 text-green-700">Pagada</Badge>;
    if (factura.estado === "parcial") return <Badge className="bg-blue-100 text-blue-700">Parcial</Badge>;
    if (factura.estado_alerta === "vencida") return <Badge className="bg-red-100 text-red-700">Vencida</Badge>;
    if (factura.estado_alerta === "critica") return <Badge className="bg-orange-100 text-orange-700">Por Vencer</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-700">Pendiente</Badge>;
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 overflow-auto">
        <div className="w-full">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Receipt className="w-7 h-7 text-[#0C314C]" />Cuentas por Pagar
              </h1>
              <p className="text-slate-500 mt-1">Gestiona las facturas pendientes de pago</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Actualizar</Button>
              {isAdmin && (
                <Button variant="outline" onClick={() => setShowConfigDialog(true)} data-testid="config-cuentas-pagar">
                  <Settings className="w-4 h-4 mr-2" />Configuración
                </Button>
              )}
              <Button onClick={handleOpenNewFactura} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
                <Plus className="w-4 h-4 mr-2" />Nueva Factura
              </Button>
            </div>
          </div>

          {/* Dashboard Cards */}
          {dashboard && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{formatCurrency(dashboard.total_pendiente)}</p>
                      <p className="text-sm text-slate-500">Total Pendiente</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(dashboard.monto_vencido)}</p>
                      <p className="text-sm text-slate-500">{dashboard.facturas_vencidas} Vencidas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-orange-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-600">{formatCurrency(dashboard.monto_proximas)}</p>
                      <p className="text-sm text-slate-500">{dashboard.facturas_proximas} Próx. 7 días</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(dashboard.pagado_mes)}</p>
                      <p className="text-sm text-slate-500">Pagado este mes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Select value={filterEstado} onValueChange={v => { setFilterEstado(v); setPage(0); }}>
                  <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="parcial">Parcial</SelectItem>
                    <SelectItem value="pagada">Pagada</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterProveedor} onValueChange={v => { setFilterProveedor(v); setPage(0); }}>
                  <SelectTrigger><SelectValue placeholder="Proveedor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los proveedores</SelectItem>
                    {proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterVencimiento} onValueChange={v => { setFilterVencimiento(v); setPage(0); }}>
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <SelectValue placeholder="Vencimiento" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las fechas</SelectItem>
                    <SelectItem value="vencidas">Vencidas</SelectItem>
                    <SelectItem value="3">Próximos 3 días</SelectItem>
                    <SelectItem value="7">Próximos 7 días</SelectItem>
                    <SelectItem value="10">Próximos 10 días</SelectItem>
                    <SelectItem value="30">Próximos 30 días</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" onClick={() => { setFilterEstado("all"); setFilterProveedor("all"); setFilterVencimiento("all"); setPage(0); }}>
                  <X className="w-4 h-4 mr-2" />Limpiar filtros
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleExportExcel} 
                  disabled={exportingExcel || facturas.length === 0}
                  className="border-green-500 text-green-600 hover:bg-green-50"
                >
                  {exportingExcel ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                  )}
                  Exportar Excel
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Invoices Table */}
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
              ) : (
                <div className="overflow-x-auto rounded-md">
                  <div className="overflow-y-auto max-h-[65vh] min-w-[1100px]" data-testid="cxp-tabla-scroll">
                  <table className="w-full min-w-[1100px]">
                    <thead>
                      <tr className="bg-slate-100 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700">
                        <th onClick={() => handleSort("factura")} className="text-left p-3 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap w-[100px] sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700" data-testid="cxp-sort-factura">Factura<SortIndicator field="factura" /></th>
                        <th onClick={() => handleSort("comprobante")} className="text-left p-3 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap w-[110px] sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700">Comprobante<SortIndicator field="comprobante" /></th>
                        <th onClick={() => handleSort("proveedor")} className="text-left p-3 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap w-[140px] sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700">Proveedor<SortIndicator field="proveedor" /></th>
                        <th onClick={() => handleSort("categoria")} className="text-left p-3 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap w-[90px] sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700">Categoría<SortIndicator field="categoria" /></th>
                        <th onClick={() => handleSort("vencimiento")} className="text-left p-3 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap w-[100px] sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700" data-testid="cxp-sort-vencimiento">Vencimiento<SortIndicator field="vencimiento" /></th>
                        <th onClick={() => handleSort("monto")} className="text-right p-3 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap w-[100px] sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700">Monto<SortIndicator field="monto" /></th>
                        <th onClick={() => handleSort("moneda")} className="text-center p-3 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap w-[60px] sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700">Moneda<SortIndicator field="moneda" /></th>
                        <th onClick={() => handleSort("saldo")} className="text-right p-3 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap w-[100px] sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700">Saldo<SortIndicator field="saldo" /></th>
                        <th onClick={() => handleSort("estado")} className="text-center p-3 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap w-[110px] sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700" data-testid="cxp-sort-estado">Estado<SortIndicator field="estado" /></th>
                        <th className="text-center p-3 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap w-[120px] sticky top-0 z-10 bg-slate-100 dark:bg-slate-800">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-900">
                      {pagedFacturas.length === 0 ? (
                        <tr><td colSpan={10} className="p-8 text-center text-slate-500 bg-slate-50 dark:bg-slate-800">No hay facturas registradas</td></tr>
                      ) : pagedFacturas.map((factura, index) => (
                        <tr key={factura.id} className={`border-b border-slate-100 dark:border-slate-800 hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors ${index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-850'}`}>
                          <td className="p-3">
                            <p className="font-medium text-slate-800 dark:text-slate-200 text-sm">{factura.numero_factura}</p>
                            <p className="text-xs text-slate-500">{factura.fecha_factura}</p>
                          </td>
                          <td className="p-3">
                            <p className="text-xs text-slate-700 dark:text-slate-300 truncate max-w-[100px]" title={factura.comprobante_fiscal}>{factura.comprobante_fiscal || "-"}</p>
                          </td>
                          <td className="p-3">
                            <p className="font-medium text-slate-800 dark:text-slate-200 text-sm truncate max-w-[130px]" title={factura.proveedor_nombre}>{factura.proveedor_nombre}</p>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="bg-white dark:bg-slate-800 text-xs">{categorias.find(c => c.value === factura.categoria)?.label || factura.categoria}</Badge>
                          </td>
                          <td className="p-3">
                            <p className={`text-sm ${factura.estado === "pagada" ? "text-slate-700 dark:text-slate-300" : factura.dias_restantes < 0 ? "text-red-600 font-medium" : factura.dias_restantes <= 3 ? "text-orange-600 font-medium" : "text-slate-700 dark:text-slate-300"}`}>
                              {factura.fecha_vencimiento}
                            </p>
                            {factura.estado === "pagada" ? (
                              <p className="text-xs text-green-600">Pagada</p>
                            ) : (
                              <p className="text-xs text-slate-500">
                                {factura.dias_restantes < 0 ? `Vencida hace ${Math.abs(factura.dias_restantes)} días` :
                                 factura.dias_restantes === 0 ? "Vence hoy" : `En ${factura.dias_restantes} días`}
                              </p>
                            )}
                          </td>
                          <td className="p-3 text-right font-medium text-slate-800 dark:text-slate-200 text-sm">{formatCurrency(factura.monto_total, factura.moneda)}</td>
                          <td className="p-3 text-center">
                            <Badge variant="outline" className="text-xs bg-white dark:bg-slate-800">{factura.moneda || "RD$"}</Badge>
                          </td>
                          <td className="p-3 text-right font-semibold text-red-600 dark:text-red-400 text-sm">
                            {formatCurrency(factura.monto_total - (factura.monto_pagado || 0), factura.moneda)}
                          </td>
                          <td className="p-3 text-center">{getEstadoBadge(factura)}</td>
                          <td className="p-3">
                            <div className="flex justify-center gap-0.5">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => viewFacturaDetalle(factura.id)} title="Ver detalles"><Eye className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => handleEditFactura(factura)} title="Editar factura" data-testid={`edit-factura-${factura.id}`}>
                                <FileText className="w-4 h-4" />
                              </Button>
                              {factura.estado !== "pagada" && (
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => { setSelectedFactura(factura); setPagoForm({...pagoForm, monto: factura.monto_total - (factura.monto_pagado || 0)}); setShowPagoDialog(true); }} title="Registrar pago">
                                  <CreditCard className="w-4 h-4" />
                                </Button>
                              )}
                              {isAdmin && factura.estado !== "pagada" && (
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteFactura(factura.id)} title="Eliminar factura"><Trash2 className="w-4 h-4" /></Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 p-4 border-t" data-testid="cxp-pagination">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span>
                    {sortedFacturas.length === 0
                      ? "0 facturas"
                      : `Mostrando ${currentPage * pageSize + 1} - ${Math.min((currentPage + 1) * pageSize, sortedFacturas.length)} de ${sortedFacturas.length}`}
                  </span>
                  <span className="hidden sm:inline">·</span>
                  <div className="flex items-center gap-1">
                    <span>Por página:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(0); }}
                      className="border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800 text-sm"
                      data-testid="cxp-page-size"
                    >
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setPage(0)}>«</Button>
                  <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Anterior</Button>
                  <span className="text-sm px-2">Página {currentPage + 1} de {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>Siguiente</Button>
                  <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Nueva Factura Dialog */}
        <Dialog open={showFacturaDialog} onOpenChange={(open) => { if (!open) { setIsEditing(false); setEditingFactura(null); resetFacturaForm(); } setShowFacturaDialog(open); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle><Receipt className="w-5 h-5 inline mr-2" />{isEditing ? "Editar Factura" : "Nueva Factura por Pagar"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div><Label>Proveedor *</Label>
                <Select value={facturaForm.proveedor_id} onValueChange={v => setFacturaForm({...facturaForm, proveedor_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar proveedor..." /></SelectTrigger>
                  <SelectContent>{proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>No. Factura *</Label><Input value={facturaForm.numero_factura} onChange={e => setFacturaForm({...facturaForm, numero_factura: e.target.value})} data-testid="factura-numero" /></div>
                <div><Label>¿Tiene Comprobante Fiscal?</Label>
                  <Select value={facturaForm.tiene_comprobante_fiscal} onValueChange={v => setFacturaForm({...facturaForm, tiene_comprobante_fiscal: v})}>
                    <SelectTrigger data-testid="factura-tiene-ncf"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="si">Sí</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Campos de NCF - Solo visibles si tiene comprobante fiscal */}
              {facturaForm.tiene_comprobante_fiscal === "si" && (
                <div className="space-y-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-blue-700 dark:text-blue-300">Comercio</Label>
                      <Input 
                        value={facturaForm.comercio_ncf} 
                        onChange={e => setFacturaForm({...facturaForm, comercio_ncf: e.target.value})} 
                        placeholder="Nombre del comercio"
                        className="mt-1"
                        data-testid="factura-comercio-ncf"
                      />
                    </div>
                    <div>
                      <Label className="text-blue-700 dark:text-blue-300"># de NCF</Label>
                      <Input 
                        value={facturaForm.numero_ncf} 
                        onChange={e => setFacturaForm({...facturaForm, numero_ncf: e.target.value})} 
                        placeholder="Ej: B0100000001"
                        className="mt-1"
                        data-testid="factura-numero-ncf"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-blue-700 dark:text-blue-300">Transacción</Label>
                    <Input 
                      value={facturaForm.transaccion} 
                      onChange={e => setFacturaForm({...facturaForm, transaccion: e.target.value})} 
                      placeholder="Número de transacción"
                      className="mt-1"
                      data-testid="factura-transaccion"
                    />
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Monto Total *</Label><Input type="number" value={facturaForm.monto_total} onChange={e => setFacturaForm({...facturaForm, monto_total: parseFloat(e.target.value) || 0})} data-testid="factura-monto" /></div>
                <div><Label>Moneda *</Label>
                  <Select value={facturaForm.moneda} onValueChange={v => setFacturaForm({...facturaForm, moneda: v})}>
                    <SelectTrigger data-testid="factura-moneda"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONEDAS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {facturaForm.moneda !== "RD$" && (
                <div className="grid grid-cols-2 gap-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3" data-testid="factura-conversion-section">
                  <div>
                    <Label className="text-emerald-700 dark:text-emerald-300">Tasa del día (RD$ por {facturaForm.moneda}) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={facturaForm.tasa_cambio || ""}
                      onChange={e => setFacturaForm({...facturaForm, tasa_cambio: parseFloat(e.target.value) || 0})}
                      placeholder="Ej: 59.50"
                      className="mt-1"
                      data-testid="factura-tasa-cambio"
                    />
                  </div>
                  <div>
                    <Label className="text-emerald-700 dark:text-emerald-300">Monto en RD$ (convertido)</Label>
                    <Input
                      readOnly
                      value={facturaForm.tasa_cambio > 0 && facturaForm.monto_total > 0
                        ? `RD$ ${(facturaForm.monto_total * facturaForm.tasa_cambio).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : "—"}
                      className="mt-1 bg-white dark:bg-slate-800 font-semibold text-emerald-700 dark:text-emerald-300"
                      data-testid="factura-monto-rd"
                    />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Fecha Factura</Label><Input type="date" value={facturaForm.fecha_factura} onChange={e => setFacturaForm({...facturaForm, fecha_factura: e.target.value})} /></div>
                <div><Label>Fecha Vencimiento *</Label><Input type="date" value={facturaForm.fecha_vencimiento} onChange={e => setFacturaForm({...facturaForm, fecha_vencimiento: e.target.value})} /></div>
              </div>
              <div><Label>Categoría</Label>
                <Select value={facturaForm.categoria} onValueChange={v => setFacturaForm({...facturaForm, categoria: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categorias.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Descripción</Label><Textarea value={facturaForm.descripcion} onChange={e => setFacturaForm({...facturaForm, descripcion: e.target.value})} /></div>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4">
                <Label className="flex items-center gap-2 mb-2"><Upload className="w-4 h-4" />Adjuntar Factura (Opcional)</Label>
                <Input type="file" accept="image/*,.pdf" onChange={e => setFacturaFile(e.target.files?.[0])} />
                {facturaFile && <p className="text-xs text-slate-500 mt-2">{facturaFile.name}</p>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFacturaDialog(false)}>Cancelar</Button>
              <Button onClick={handleCreateFactura} disabled={saving} className="bg-[#0C314C] text-white">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Registrar Pago Dialog */}
        <Dialog open={showPagoDialog} onOpenChange={setShowPagoDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle><CreditCard className="w-5 h-5 inline mr-2" />Registrar Pago</DialogTitle></DialogHeader>
            {selectedFactura && (
              <div className="bg-slate-50 p-3 rounded-lg mb-4">
                <p className="font-medium">Factura: {selectedFactura.numero_factura}</p>
                <p className="text-sm text-slate-600">Proveedor: {selectedFactura.proveedor_nombre}</p>
                <p className="text-sm text-slate-600">Saldo pendiente: {formatCurrency(selectedFactura.monto_total - (selectedFactura.monto_pagado || 0), selectedFactura.moneda)}</p>
              </div>
            )}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Monto a Pagar *</Label><Input type="number" value={pagoForm.monto} onChange={e => setPagoForm({...pagoForm, monto: parseFloat(e.target.value) || 0})} /></div>
                <div><Label>Fecha de Pago *</Label><Input type="date" value={pagoForm.fecha_pago} onChange={e => setPagoForm({...pagoForm, fecha_pago: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Método de Pago *</Label>
                  <Select value={pagoForm.metodo_pago} onValueChange={v => setPagoForm({...pagoForm, metodo_pago: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>{metodosPago.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Número Transacción</Label><Input data-testid="pago-numero-transaccion-input" value={pagoForm.numero_referencia} onChange={e => setPagoForm({...pagoForm, numero_referencia: e.target.value})} placeholder="Cheque/Transferencia" /></div>
              </div>
              {pagoForm.metodo_pago === "tarjeta" && (
                <div><Label>Terminal de Tarjeta</Label><Input data-testid="pago-terminal-tarjeta-input" value={pagoForm.terminal_tarjeta} onChange={e => setPagoForm({...pagoForm, terminal_tarjeta: e.target.value})} placeholder="Últimos dígitos de la tarjeta" /></div>
              )}
              <div><Label>Notas</Label><Textarea value={pagoForm.notas} onChange={e => setPagoForm({...pagoForm, notas: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPagoDialog(false)}>Cancelar</Button>
              <Button onClick={handleRegistrarPago} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Registrar Pago</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detalle Factura Dialog */}
        <Dialog open={showDetalleDialog} onOpenChange={setShowDetalleDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle><FileText className="w-5 h-5 inline mr-2" />Detalle de Factura</DialogTitle></DialogHeader>
            {selectedFactura && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Proveedor</p>
                    <p className="font-medium">{selectedFactura.proveedor_nombre}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">No. Factura</p>
                    <p className="font-medium">{selectedFactura.numero_factura}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Monto Total</p>
                    <p className="font-medium text-lg">{formatCurrency(selectedFactura.monto_total, selectedFactura.moneda)}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Saldo Pendiente</p>
                    <p className="font-medium text-lg text-red-600">{formatCurrency(selectedFactura.saldo_pendiente, selectedFactura.moneda)}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Fecha Vencimiento</p>
                    <p className="font-medium">{selectedFactura.fecha_vencimiento}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Estado</p>
                    {getEstadoBadge(selectedFactura)}
                  </div>
                </div>
                {selectedFactura.descripcion && <div className="bg-slate-50 p-4 rounded-lg"><p className="text-sm text-slate-500">Descripción</p><p>{selectedFactura.descripcion}</p></div>}
                {selectedFactura.archivo_url && (
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500 mb-2">Archivo Adjunto</p>
                    <a href={selectedFactura.archivo_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1"><Image className="w-4 h-4" />Ver factura</a>
                  </div>
                )}
                <div>
                  <h4 className="font-medium mb-2">Historial de Pagos</h4>
                  {selectedFactura.pagos?.length === 0 ? (
                    <p className="text-slate-500 text-sm">No hay pagos registrados</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedFactura.pagos?.map(pago => (
                        <div key={pago.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div>
                            <p className="font-medium text-green-700">{formatCurrency(pago.monto, selectedFactura.moneda)}</p>
                            <p className="text-xs text-slate-500">{pago.fecha_pago} - {metodosPago.find(m => m.value === pago.metodo_pago)?.label || pago.metodo_pago}</p>
                          </div>
                          {pago.numero_referencia && <Badge variant="outline">{pago.numero_referencia}</Badge>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              {selectedFactura?.saldo_pendiente > 0 && (
                <Button onClick={() => { setPagoForm({...pagoForm, monto: selectedFactura.saldo_pendiente}); setShowPagoDialog(true); }} className="bg-green-600 text-white">
                  <CreditCard className="w-4 h-4 mr-2" />Registrar Pago
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowDetalleDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Configuración Dialog */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuración de Cuentas por Pagar
              </DialogTitle>
            </DialogHeader>
            
            <Tabs value={configTab} onValueChange={setConfigTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="categorias">Categorías</TabsTrigger>
                <TabsTrigger value="monedas">Monedas</TabsTrigger>
              </TabsList>
              
              {/* Categorías Tab */}
              <TabsContent value="categorias" className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Nueva Categoría</h4>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <Label>Nombre *</Label>
                      <Input 
                        value={newCategoria.nombre} 
                        onChange={e => setNewCategoria({...newCategoria, nombre: e.target.value})}
                        placeholder="Ej: Servicios Profesionales"
                      />
                    </div>
                    <div>
                      <Label>Descripción</Label>
                      <Input 
                        value={newCategoria.descripcion} 
                        onChange={e => setNewCategoria({...newCategoria, descripcion: e.target.value})}
                        placeholder="Descripción opcional"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={async () => {
                      if (!newCategoria.nombre) { toast.error("Ingrese un nombre"); return; }
                      setSavingConfig(true);
                      try {
                        await axios.post(`${API}/cuentas-pagar/categorias`, newCategoria, getAuthHeaders());
                        toast.success("Categoría creada");
                        setNewCategoria({ nombre: "", descripcion: "" });
                        fetchData();
                      } catch (err) { toast.error("Error al crear categoría"); }
                      finally { setSavingConfig(false); }
                    }}
                    disabled={savingConfig}
                    size="sm"
                    className="bg-[#0C314C] text-white"
                  >
                    {savingConfig && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <Plus className="w-4 h-4 mr-2" />Agregar
                  </Button>
                </div>
                
                <div className="border rounded-lg">
                  <h4 className="font-medium p-3 border-b bg-slate-50">Categorías Existentes</h4>
                  <ScrollArea className="h-48">
                    <div className="p-3 space-y-2">
                      {categorias.map((cat, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div>
                            <p className="font-medium">{cat.label}</p>
                            <p className="text-xs text-slate-500">{cat.value}</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500"
                            onClick={async () => {
                              try {
                                await axios.delete(`${API}/cuentas-pagar/categorias/${cat.value}`, getAuthHeaders());
                                toast.success("Categoría eliminada");
                                fetchData();
                              } catch (err) { toast.error("Error al eliminar"); }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {categorias.length === 0 && (
                        <p className="text-center text-slate-500 py-4">No hay categorías configuradas</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </TabsContent>
              
              {/* Monedas Tab */}
              <TabsContent value="monedas" className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Monedas Disponibles</h4>
                  <p className="text-sm text-slate-500 mb-4">
                    Las siguientes monedas están disponibles para las facturas por pagar:
                  </p>
                  <div className="space-y-2">
                    {MONEDAS.map((moneda, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded border">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="text-lg font-bold">{moneda.value}</Badge>
                          <span>{moneda.label}</span>
                        </div>
                        {moneda.value === "RD$" && (
                          <Badge className="bg-green-100 text-green-700">Por defecto</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-700">
                    <strong>Nota:</strong> Para agregar más monedas, contacte al administrador del sistema.
                    Las tasas de cambio deben configurarse manualmente al registrar pagos en monedas diferentes.
                  </p>
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
